import { useCallback, useEffect, useRef } from "react";

interface UseLazyLoadSentinelConfig {
  fetchMore?: () => void;
  isEnabled: boolean;
}

export function useLazyLoadSentinel({
  fetchMore,
  isEnabled,
}: UseLazyLoadSentinelConfig) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const fetchMoreRef = useRef(fetchMore);
  fetchMoreRef.current = fetchMore;

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !isEnabled) return;

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting) fetchMoreRef.current?.();
        },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [isEnabled]
  );

  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return sentinelRef;
}
